import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  ApiVisa, FinancialItemContainerModel, FinancialItemService, Pagination
} from 'blg-akaun-ts-lib';
import { ReplaySubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AppConfig } from '../../shared-utilities/visa';

@Component({
  selector: 'blg-select-fi-item-drop-down',
  templateUrl: './select-fi-item-drop-down.component.html',
  styleUrls: ['./select-fi-item-drop-down.component.scss']
})
export class SelectFiItemDropDownComponent implements OnInit, OnDestroy {

  @Input() fi: FormControl;
  apiVisa = AppConfig.apiVisa;
  @Input() txnType : string;
  @Input() multiple = false;
  @Input() label = 'Financial Item';

  @Output() fiChange = new EventEmitter<FormControl>();

  serverSideFiltering = new FormControl();
  serverSideSearching = false;
  filteredOptions$: ReplaySubject<FinancialItemContainerModel[]> = new ReplaySubject<FinancialItemContainerModel[]>(1);
  inventoryList: FinancialItemContainerModel[] = [];
  selectedInventory = new FinancialItemContainerModel();

  protected _onDestroy = new Subject<void>();

  constructor( private fiItemService: FinancialItemService) { }

  ngOnInit() {
    if (this.fi.value) {
      this.fiItemService.getByGuid(this.fi.value, this.apiVisa).pipe(
        takeUntil(this._onDestroy)
      ).subscribe( resolved => {
        this.inventoryList = [resolved.data];
        this.filteredOptions$.next([resolved.data]);
        this.onSelect(resolved.data.bl_fi_mst_item_hdr.guid.toString());
      });
    }
    this.serverSideFiltering.valueChanges.pipe(
        filter(searchText => searchText.length > 3),
        tap(() => this.serverSideSearching = true),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(searchText => this.searchfinancialItem(searchText))
      ).subscribe( resolved => {
        this.serverSideSearching = false;
        if (this.selectedInventory) {
          this.filteredOptions$.next([...resolved, this.selectedInventory]);
          this.inventoryList = [...resolved, this.selectedInventory];
        } else {
          this.filteredOptions$.next(resolved);
          this.inventoryList = resolved;
        }
      }, error => {
        this.serverSideSearching = false;
        console.error(error);
      });
  }

  /**
   * Server-side financial-item typeahead.
   *
   * Replaces the raw SELECT that went through SubQueryService:
   *
   *   FROM bl_fi_mst_item_hdr hdr
   *   WHERE hdr.status != 'DELETED'
   *     AND (hdr.code ILIKE '%x%' OR hdr.name ILIKE '%x%')
   *   ORDER BY hdr.name, hdr.guid
   *
   * GET fi/fi-items/query -> FinancialItemUow.generateWhereClauseByCriteria
   * honours `search_word` (FinancialItemHeaderUowHelper.generateClauseForWholeSearch_word,
   * bound and ILIKE over code, name and the other header text columns) and, in
   * FinancialItemHeaderUowHelper.generateHeaderWhereClause, defaults to
   * `status != 'DELETED'` when no status is sent.
   *
   * Behaviour notes:
   * - `search_word` matches more header columns than the old code/name pair
   *   (category, txn_type, descr, remarks, alt item codes, scan_code, ...), so the
   *   typeahead is broader.
   * - The `txnType` @Input stays unused, exactly as before. The legacy guard read
   *   `(txnType != null || txnType != undefined) || txnType != ''`, which is true
   *   for every value, so the `AND hdr.txn_type = ...` branch never ran. It is
   *   left inert rather than silently switched on here; the criterion to send if
   *   this is ever meant to work is `txn_type` (honoured by
   *   generateHeaderWhereClause).
   * - One request instead of one subquery plus one getByCriteria per hit. The old
   *   per-hit call also passed `guids` as a single value; that path returned the
   *   containers one at a time and is no longer needed.
   */
  searchfinancialItem(searchText: string) {
    return this.fiItemService.getByCriteria(
      new Pagination(0, 100, [
        { columnName: 'search_word', operator: '=', value: (searchText || '').trim() }
      ]),
      this.apiVisa
    ).pipe(
      map(response => response.data)
    );
  }

  onSelect(branchGuid: string) {
    this.fi.patchValue(branchGuid);
    this.selectedInventory = this.inventoryList.find( inv => inv.bl_fi_mst_item_hdr.guid === branchGuid);
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

}
